import { createApi } from '@reduxjs/toolkit/query/react';
import { PaginationIF } from '@/types/common';
import { FavoriteIF } from '@/api/favoritesApi/types';
import { fetchRestApiQuery } from '@/helpers/api';
import { FavoriteEntityT } from '@/constants/favoriteEntities';

export const favoritesApi = createApi({
    reducerPath: 'favoritesApi',
    baseQuery: fetchRestApiQuery('/Likes'),
    tagTypes: ['FavoritesList'],
    endpoints: (builder) => ({
        getFavoritesList: builder.query<
            PaginationIF<FavoriteIF>,
            FavoriteEntityT
        >({
            query: (guid) => ({
                url: `/${guid}`,
            }),
            providesTags: (_result, _error, guid) => [
                { type: 'FavoritesList', id: guid },
            ],
        }),
        addToFavorites: builder.mutation<void, FavoriteIF>({
            query: ({ guid, entityGuid }) => ({
                url: `/${guid}`,
                method: 'POST',
                body: {
                    entityGuid,
                },
            }),
            onQueryStarted: async (
                { guid, entityGuid },
                { dispatch, queryFulfilled },
            ) => {
                const patchResult = dispatch(
                    favoritesApi.util.updateQueryData(
                        'getFavoritesList',
                        guid,
                        (draft) => {
                            if (draft?.data) {
                                const optimisticItem: FavoriteIF = {
                                    guid,
                                    entityGuid,
                                };

                                draft.data.push(optimisticItem);
                                draft.total = (draft.total || 0) + 1;
                            }
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: (_result, _error, { guid }) => [
                { type: 'FavoritesList', id: guid },
            ],
        }),
        removeFromFavorites: builder.mutation<void, FavoriteIF>({
            query: ({ guid, entityGuid }) => ({
                url: `/${guid}/${entityGuid}`,
                method: 'DELETE',
            }),
            onQueryStarted: async (
                { guid, entityGuid },
                { dispatch, queryFulfilled },
            ) => {
                const patchResult = dispatch(
                    favoritesApi.util.updateQueryData(
                        'getFavoritesList',
                        guid,
                        (draft) => {
                            if (draft?.data) {
                                const itemIndex = draft.data.findIndex(
                                    (item) =>
                                        item.entityGuid === entityGuid &&
                                        item.guid === guid,
                                );

                                if (itemIndex !== -1) {
                                    draft.data.splice(itemIndex, 1);
                                    draft.total = Math.max(
                                        (draft.total || 0) - 1,
                                        0,
                                    );
                                }
                            }
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: (_result, _error, { guid }) => [
                { type: 'FavoritesList', id: guid },
            ],
        }),
    }),
});
